import {Dispatch, FC, SetStateAction, useEffect, useState} from 'react'
import {CustomInputs} from '../../../../../../../_metronic/helpers/components/CustomInputs'
import {numberWithCommas} from '../../../../../../../_metronic/helpers'
import {InvoicePaymentForm, InvoicePaymentRegistrationInfo} from '../../core/_models'
import {InvoicePaymentEditModal} from '../payment/InvoicePaymentEditModal'
import {InvoicePaymentRow} from '../payment/InvoicePaymentRow'
import {getPaymentTaxValidationError, nowPersianDateTime} from '../../core/paymentTaxValidation'
import {getTemplate11CashError, isTemplate11} from '../../core/invoiceTemplate11'
import {
  InvoiceEmptyState,
  InvoiceFinancialSummary,
  InvoiceSectionHeader,
  InvoiceSummaryItem,
} from '../components/InvoicePresentation'

type props = {
  settlement: {
    settlement: string
    setSettlement: Dispatch<SetStateAction<string>>
  }
  payments: {
    payments: InvoicePaymentForm[]
    setPayments: Dispatch<SetStateAction<InvoicePaymentForm[]>>
  }
  // صورتحساب ارسال‌شده: پرداخت‌های جدید پس از ذخیره در سامانه مؤدیان ثبت می‌شوند (invoice-payment)
  paymentRegistration?: InvoicePaymentRegistrationInfo
  paymentsToRegisterCount?: number
  onRetryRegistration?: (registrationId: number) => void
  retryingRegistrationId?: number
  getSumPayments: () => number
  getSumNetPrice: () => number
  invoiceDate: string
  invoiceType: {
    invoiceType: number
    setInvoiceType: Dispatch<SetStateAction<number>>
  }
  totalVatRate?: number
  invoiceTemplate?: number
  cash?: {
    cash: string
    setCash: Dispatch<SetStateAction<string>>
  }
  // صورتحساب ارسال‌شده در «عملیات پرداخت»: روش تسویه همان است که به سامانه رفته؛ نه نشان داده می‌شود
  // و نه با جدول پرداخت تغییر می‌کند
  settlementReadOnly?: boolean
  // «پیش‌فاکتور»: nothing is paid against an offer; payments go on the invoice it becomes.
  hidePayments?: boolean
}

const Step3: FC<props> = ({
  settlement,
  payments,
  paymentRegistration,
  paymentsToRegisterCount = 0,
  onRetryRegistration,
  retryingRegistrationId,
  getSumPayments,
  getSumNetPrice,
  invoiceDate,
  invoiceType,
  totalVatRate = 0,
  invoiceTemplate,
  cash,
  settlementReadOnly = false,
  hidePayments = false,
}) => {
  const [editPaymentItem, setEditPaymentItem] = useState<InvoicePaymentForm | null | undefined>(
    undefined
  )
  // الگوی ۱۱ (بورس کالا): اطلاعات پرداخت خارج از الگوست؛ در روش نقد و نسیه فقط سهم نقدی گرفته می‌شود
  const template11 = isTemplate11(invoiceType.invoiceType, invoiceTemplate)
  // صورتحساب ارسال‌شده به سامانه: ردیف‌های قبلی قفل‌اند و فقط پرداخت جدید اضافه می‌شود
  const isSentInvoice = paymentRegistration?.lk === true
  const registersOnSave = paymentRegistration?.el === true

  useEffect(() => {
    // پرداخت کامل → نقدی؛ روی صورتحساب خالی (هنوز بارگذاری‌نشده) و صورتحساب ارسال‌شده اعمال نمی‌شود
    if (
      !settlementReadOnly &&
      settlement.settlement != '1' &&
      getSumNetPrice() > 0 &&
      getSumNetPrice() - getSumPayments() == 0
    ) {
      settlement.setSettlement('1')
    }
  }, [payments])

  const deletePayment = (rw: InvoicePaymentForm) => {
    if (window.confirm('آیا از حذف پرداختی اطمینان دارید؟')) {
      const ind = payments.payments.findIndex((x) => x.id == rw.id)
      payments.payments.splice(ind, 1)
      payments.setPayments([...[], ...payments.payments])
    }
  }

  const invoiceTotal = getSumNetPrice()
  const paymentsTotal = getSumPayments()
  const remaining = invoiceTotal - paymentsTotal
  const template11CashError =
    template11 && cash ? getTemplate11CashError(settlement.settlement, cash.cash, invoiceTotal) : ''
  const template11CreditShare =
    template11 && cash && /^\d+$/.test((cash.cash || '').trim())
      ? Math.max(invoiceTotal - Number(cash.cash), 0).toString()
      : ''
  const isCreditSettlement = settlement.settlement == '2'
  const isSettled = remaining == 0
  const isOverpaid = remaining < 0
  const showReference = payments.payments.some(
    (payment) => !!(payment.traceNo || payment.refereceNo || payment.trackingNumber)
  )
  const columnCount = 6 + (showReference ? 1 : 0)
  // روی صورتحساب ارسال‌شدهٔ نسیه هم می‌شود پرداخت افزود؛ با اولین پرداخت، تسویه «نقدی/نسیه» می‌شود
  const addPaymentDisabled =
    settlement.settlement == '0' ||
    (isCreditSettlement && !isSentInvoice) ||
    invoiceTotal == 0 ||
    remaining == 0
  const invalidPaymentCount = payments.payments.filter(
    (payment) =>
      !payment.locked &&
      getPaymentTaxValidationError({
        ...payment,
        invoiceNetPrice: invoiceTotal,
        invoiceIssueDate: isSentInvoice ? invoiceDate : undefined,
        nowDateTime: isSentInvoice ? nowPersianDateTime() : undefined,
      })
  ).length

  const paymentSummaryItems: InvoiceSummaryItem[] = [
    {
      key: 'invoice-total',
      label: 'مبلغ نهایی صورتحساب',
      value: numberWithCommas(invoiceTotal),
      unit: 'ریال',
    },
    {
      key: 'payments-total',
      label: 'مجموع پرداخت‌های ثبت‌شده',
      value: numberWithCommas(paymentsTotal),
      unit: 'ریال',
    },
    {
      key: 'payment-count',
      label: 'تعداد پرداخت‌ها',
      value: numberWithCommas(payments.payments.length),
    },
  ]
  if (!isCreditSettlement && remaining > 0) {
    paymentSummaryItems.push({
      key: 'remaining',
      label: 'مبلغ باقی‌مانده',
      value: numberWithCommas(remaining),
      unit: 'ریال',
      tone: 'warning',
      emphasis: true,
    })
  }
  if (!isCreditSettlement && isOverpaid) {
    paymentSummaryItems.push({
      key: 'overpaid',
      label: 'مبلغ اضافه‌پرداخت',
      value: numberWithCommas(Math.abs(remaining)),
      unit: 'ریال',
      tone: 'danger',
      emphasis: true,
    })
  }

  const paymentStatus = isCreditSettlement
    ? 'تسویه نسیه'
    : isSettled
    ? 'تسویه کامل'
    : isOverpaid
    ? 'اضافه‌پرداخت'
    : 'تسویه ناقص'
  const paymentStatusClass = isCreditSettlement
    ? 'badge-light-info'
    : isSettled
    ? 'badge-light-success'
    : isOverpaid
    ? 'badge-light-danger'
    : 'badge-light-warning'
  const paymentWarning = invalidPaymentCount
    ? `${numberWithCommas(invalidPaymentCount)} پرداخت دارای خطاست و به سازمان ارسال نخواهد شد.`
    : !isCreditSettlement && !isSettled
    ? isOverpaid
      ? 'مجموع پرداخت‌های ثبت‌شده از مبلغ نهایی صورتحساب بیشتر است.'
      : 'مجموع پرداخت‌های ثبت‌شده با مبلغ نهایی صورتحساب مطابقت ندارد.'
    : undefined

  return (
    <>
      {!settlementReadOnly && (
        <section className='mp-invoice-section mp-invoice-section--compact'>
          <InvoiceSectionHeader
            title='روش تسویه صورتحساب'
            helpKey='header.setm'
            description='روش تسویه را متناسب با پرداخت‌های ثبت‌شده انتخاب کنید.'
          />
          <div className='row'>
            <div className='col-lg-6 col-12'>
              <CustomInputs
                key={invoiceType.invoiceType}
                type='select'
                items={[
                  {label: 'نقدی', value: '1'},
                  {
                    label: 'نسیه',
                    value: '2',
                    disabled: template11
                      ? invoiceTotal == 0
                      : invoiceTotal == 0 ||
                        payments.payments.length > 0 ||
                        invoiceType.invoiceType == 2,
                  },
                  {
                    label: 'نقدی/نسیه',
                    value: '3',
                    disabled: template11
                      ? invoiceTotal == 0
                      : invoiceTotal == 0 || remaining == 0 || invoiceType.invoiceType == 2,
                  },
                ]}
                isRequired
                label='نحوه تسویه'
                helpKey='header.setm'
                stateValue={{
                  value: settlement.settlement,
                  setValue: settlement.setSettlement,
                }}
              />
            </div>
          </div>
        </section>
      )}

      {template11 && (
        <section className='mp-invoice-section mp-invoice-section--compact'>
          <InvoiceSectionHeader
            title='سهم نقدی و نسیه'
            description='در الگوی بورس کالا اطلاعات پرداخت خارج از الگوست و ارسال نمی‌شود.'
          />
          {settlement.settlement == '3' && cash ? (
            <div className='row'>
              <div className='col-lg-6 col-12'>
                <CustomInputs
                  type='currency'
                  isRequired
                  label='سهم نقدی (ریال)'
                  stateValue={{value: cash.cash, setValue: cash.setCash}}
                  error={template11CashError || undefined}
                  hint='عدد صحیح ریال؛ بزرگ‌تر از صفر و کمتر از مبلغ نهایی صورتحساب.'
                />
              </div>
              <div className='col-lg-6 col-12'>
                <CustomInputs
                  key={template11CreditShare}
                  type='currency'
                  isDisabled
                  label='سهم نسیه (ریال)'
                  value={template11CreditShare}
                  hint='برابر مبلغ نهایی صورتحساب منهای سهم نقدی.'
                />
              </div>
            </div>
          ) : (
            <div className='text-gray-600'>
              {settlement.settlement == '2'
                ? 'کل مبلغ صورتحساب نسیه ثبت می‌شود.'
                : 'کل مبلغ صورتحساب نقدی ثبت می‌شود.'}
            </div>
          )}
        </section>
      )}

      {hidePayments && (
        <div className='alert alert-light-info mb-0' role='note'>
          پیش‌فاکتور پرداخت ندارد؛ پرداخت‌ها پس از تبدیل، روی صورتحساب ثبت می‌شوند.
        </div>
      )}
      {!template11 && !hidePayments && (
        <>
          <section className='mp-invoice-section' aria-labelledby='invoice-payments-title'>
            <InvoiceSectionHeader
              title={<span id='invoice-payments-title'>اطلاعات پرداخت</span>}
              helpKey='section.payments'
              description='اطلاعات اصلی پرداخت‌ها در جدول و اطلاعات بانکی یا تخصصی در جزئیات هر ردیف نمایش داده می‌شود.'
              eyebrow={`${numberWithCommas(payments.payments.length)} پرداخت`}
              actions={
                <button
                  type='button'
                  disabled={addPaymentDisabled}
                  className={`btn btn-sm ${
                    addPaymentDisabled ? 'btn-light-secondary' : 'btn-primary'
                  }`}
                  onClick={() => setEditPaymentItem(null)}
                >
                  <i className='las la-plus' aria-hidden='true'></i>
                  افزودن پرداخت
                </button>
              }
            />
            <div className='mp-invoice-data-table-wrap'>
              <table className='table mp-invoice-data-table mp-invoice-payments-table'>
                <thead>
                  <tr>
                    <th className='mp-invoice-col--index text-center'>ردیف</th>
                    <th className='mp-invoice-col--item text-start'>روش پرداخت</th>
                    <th className='mp-invoice-col--money text-center'>مبلغ پرداخت</th>
                    <th className='mp-invoice-col--date text-center'>تاریخ پرداخت</th>
                    {showReference && (
                      <th className='mp-invoice-col--reference text-center'>
                        شماره پیگیری یا مرجع
                      </th>
                    )}
                    <th className='mp-invoice-col--status text-center'>وضعیت</th>
                    <th className='mp-invoice-col--actions text-center'>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.payments.map((payment, index) => {
                    // ردیف قفل‌شده (ثبت‌شده در سامانه) دیگر با قواعد ارسال همراه صورتحساب سنجیده نمی‌شود
                    const taxValidationError = payment.locked
                      ? ''
                      : getPaymentTaxValidationError({
                          ...payment,
                          invoiceNetPrice: invoiceTotal,
                          // روی صورتحساب ارسال‌شده، پرداخت قبل از صدور پذیرفته نمی‌شود
                          invoiceIssueDate: isSentInvoice ? invoiceDate : undefined,
                        })
                    return (
                      <InvoicePaymentRow
                        key={payment.id || index}
                        payment={payment}
                        index={index}
                        columnCount={columnCount}
                        showReference={showReference}
                        taxValidationError={taxValidationError}
                        isEditing={editPaymentItem?.id === payment.id}
                        onEdit={() => setEditPaymentItem(payment)}
                        onDelete={() => deletePayment(payment)}
                        onRetryRegistration={onRetryRegistration}
                        retryingRegistrationId={retryingRegistrationId}
                        isSentInvoice={isSentInvoice}
                      />
                    )
                  })}
                  {payments.payments.length === 0 && (
                    <tr className='mp-invoice-data-empty-row'>
                      <td colSpan={columnCount}>
                        <InvoiceEmptyState
                          title='هنوز پرداختی ثبت نشده است'
                          description={
                            isCreditSettlement
                              ? 'برای تسویه نسیه، ثبت پرداخت در این مرحله الزامی نیست.'
                              : 'در صورت انجام پرداخت، اطلاعات آن را از این بخش اضافه کنید.'
                          }
                          icon='la-credit-card'
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <InvoiceFinancialSummary
            title='خلاصه وضعیت پرداخت'
            description='این مقادیر از اطلاعات فعلی صورتحساب و پرداخت‌های ثبت‌شده دریافت می‌شوند.'
            items={paymentSummaryItems}
            status={
              <span className={`badge ${paymentStatusClass}`}>
                <i
                  className={`las ${
                    isSettled
                      ? 'la-check-circle'
                      : isCreditSettlement
                      ? 'la-clock'
                      : 'la-info-circle'
                  }`}
                  aria-hidden='true'
                ></i>
                {paymentStatus}
              </span>
            }
            warning={paymentWarning}
          />

          {isSentInvoice && (
            <section className='mp-invoice-section mp-invoice-section--compact'>
              {registersOnSave ? (
                <div className='alert alert-info d-flex align-items-start mb-0 py-3' role='status'>
                  <i className='las la-cloud-upload-alt fs-2 me-2' aria-hidden='true'></i>
                  <div>
                    <div className='fw-bold'>ثبت پرداخت در سامانه مؤدیان</div>
                    <div className='fs-7'>
                      {paymentsToRegisterCount > 0
                        ? `${numberWithCommas(
                            paymentsToRegisterCount
                          )} پرداخت جدید پس از ذخیره، به‌صورت خودکار در کارپوشهٔ سامانه مؤدیان ثبت می‌شود. ردیف‌های ثبت‌شده دیگر قابل ویرایش یا حذف نیستند.`
                        : 'پرداخت‌هایی که به این صورتحساب اضافه کنید، پس از ذخیره به‌صورت خودکار در کارپوشهٔ سامانه مؤدیان ثبت می‌شوند. ردیف‌هایی که سازمان دارد قفل هستند.'}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className='alert alert-warning d-flex align-items-start mb-0 py-3'
                  role='status'
                >
                  <i className='las la-info-circle fs-2 me-2' aria-hidden='true'></i>
                  <div>
                    <div className='fw-bold'>
                      پرداخت‌های این صورتحساب در سامانه مؤدیان ثبت نمی‌شوند
                    </div>
                    <div className='fs-7'>
                      {paymentRegistration?.rs ||
                        'این صورتحساب شرایط ثبت پرداخت در سامانه مؤدیان را ندارد؛ پرداخت‌ها فقط در سوابق شما ذخیره می‌شوند.'}
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {editPaymentItem !== undefined && (
        <InvoicePaymentEditModal
          getSumNetPrice={getSumNetPrice}
          getSumPayments={getSumPayments}
          payments={payments}
          invoiceDate={invoiceDate}
          enforceInvoiceDate={isSentInvoice}
          paymentItem={{paymentItem: editPaymentItem, setPaymentItem: setEditPaymentItem}}
          totalVatRate={totalVatRate}
        />
      )}
    </>
  )
}

export {Step3}
